import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@tanstack/react-router';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils/cn';
import {
  groupHoverFill,
  iconSize,
  restingFg,
  sectionSurface,
  selectedSurface,
} from './styles';
import type { NavGroup } from './types';

// Both shapes draw from the same tokens, so an icon never changes brightness
// or size when the sidebar collapses.
//   active   = a page under this group is open
//   selected = this group's flyout is the one currently open
const pillActive = selectedSurface;
const pillSelected = sectionSurface;
const pillInactive = `${restingFg} ${groupHoverFill}`;

export function StripGroup({
  group,
  selected,
  anyChildActive,
  firstChildHref,
  showTooltip,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  group: NavGroup;
  selected: boolean;
  anyChildActive: boolean;
  firstChildHref?: string;
  /**
   * False only while this group's own hover flyout is showing — that panel
   * already names the group and a tooltip would render on top of it. Every
   * other state (panel pinned, or unpinned before the flyout opens) shows the
   * tooltip, so the icon is never nameless on hover.
   */
  showTooltip: boolean;
  onClick: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  const label = group.shortLabel ?? group.label;
  const Icon = group.icon;
  const inner = (
    <span
      className={cn(
        'flex items-center justify-center size-9 min-[1600px]:size-10 rounded-lg transition-colors',
        anyChildActive ? pillActive : selected ? pillSelected : pillInactive,
      )}
    >
      <FontAwesomeIcon icon={Icon} className={iconSize} />
    </span>
  );

  const shared = {
    'aria-label': label,
    onClick,
    onMouseEnter,
    onMouseLeave,
    className: 'group flex justify-center w-full cursor-pointer',
  };
  // Always a shortcut to the group's first page: clicking navigates there and
  // expands the column onto the section. `selected` only means this group's
  // flyout is the one open, which must not disable navigation.
  const linkHref = firstChildHref;

  if (!showTooltip) {
    return linkHref ? (
      <Link to={linkHref as never} {...shared}>
        {inner}
      </Link>
    ) : (
      <button type='button' {...shared}>
        {inner}
      </button>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          linkHref ? (
            <Link to={linkHref as never} {...shared} />
          ) : (
            <button type='button' {...shared} />
          )
        }
      >
        {inner}
      </TooltipTrigger>
      <TooltipContent side='right'>{label}</TooltipContent>
    </Tooltip>
  );
}
